import React, { createContext, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { wait } from "../utils/helpers";
import { ToastOptions } from "../types/toast";

type NotificationContextType = {
  addNotification: (props: ToastOptions) => void;
  removeNotification?: (id: string | number | Uint32Array) => void;
};

export const NotificationContext = createContext<NotificationContextType>({
  addNotification: () => true
});

export const NotificationContextProvider = ({ children }: any) => {
  const lastNotificationRef = useRef<ToastOptions | null>(null);

  const addNotification = useCallback((newNotification: ToastOptions) => {
    if (
      lastNotificationRef.current &&
      lastNotificationRef.current.heading === newNotification.heading
    ) {
      return;
    }

    lastNotificationRef.current = newNotification;

    let defaultTime = 3000;

    const message = JSON.stringify(newNotification.heading);

    if (message.length > 100 && message.length <= 150) {
      defaultTime = 4000;
    } else if (message.length > 150) {
      defaultTime = 5000;
    }

    const autoClose =
      newNotification.autoClose === undefined
        ? defaultTime
        : newNotification.autoClose;

    const duration = autoClose === false ? Infinity : autoClose;

    switch (newNotification.toastType) {
      case "success":
        toast.success(newNotification.heading, { duration });
        break;
      case "error":
        toast.error(newNotification.heading, { duration });
        break;
      case "warning":
        toast.warning(newNotification.heading, { duration });
        break;
      case "information":
      case "info":
        toast.info(newNotification.heading, { duration });
        break;
      default:
        toast(newNotification.heading, { duration });
    }

    wait(autoClose || 0).then(() => {
      lastNotificationRef.current = null;
    });
  }, []);

  const notificationContextValue = useMemo(
    () => ({
      addNotification
    }),
    [addNotification]
  );

  return (
    <NotificationContext.Provider value={notificationContextValue}>
      {children}
      <Toaster position="bottom-right" />
    </NotificationContext.Provider>
  );
};

export const notification = (props: ToastOptions) => {
  const toastNotification = new CustomEvent<ToastOptions>("toastNotification", {
    detail: props
  });
  window.dispatchEvent(toastNotification);
};
